import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const faqItems = [
	{
		question: "Is Proxi live yet?",
		answer:
			"We're in public beta, onboarding customers and runners from our Lagos waitlist first. Join the waitlist to get early access when we open your area.",
	},
	{
		question: "How does escrow actually work?",
		answer:
			"Once you and a runner agree on a price in chat, the money moves from your Proxi wallet into a secure hold. The runner can see the funds are locked and starts the errand. The money is only released when you confirm the delivery with a one-time code.",
	},
	{
		question: "How do I fund my wallet?",
		answer:
			"Top up by bank transfer or card from any Nigerian bank. Deposits land in your wallet in seconds, powered by Paystack.",
	},
	{
		question: "What happens if something goes wrong?",
		answer:
			"If an errand goes sideways — wrong item, damaged goods, a runner that goes silent — raise a dispute in the app before confirming the task. Our support team reviews the chat history and resolves the case fairly, so no one walks away unfairly charged.",
	},
	{
		question: "What if I need to cancel?",
		answer:
			"You can cancel any errand before a runner has committed, free of charge. Once funds are locked and the runner is on the way, a small cancellation fee applies. If the runner is the one who goes silent, you can appeal the fee through support.",
	},
	{
		question: "What makes Proxi Private different?",
		answer:
			"Proxi Private is our premium tier for sensitive errands. Runners in this tier pass rigorous background checks, sign Non-Disclosure Agreements (NDAs), and your items are covered by insurance. First-time Private users complete a quick one-time ID verification.",
	},
	{
		question: "What is Proxi Premium?",
		answer:
			"Proxi Premium is a monthly subscription at ₦7,500/month. You get a ₦3,000 wallet credit every month, priority support within 2 minutes, and Multi-Chat — the ability to negotiate with up to 3 runners at the same time for a single errand.",
	},
	{
		question: "How is my personal information protected?",
		answer:
			"All chat and calls happen inside the Proxi app, so your phone number and WhatsApp never leave your account. Private-tier chats use end-to-end encryption.",
	},
	{
		question: "How do referral rewards work?",
		answer:
			"Refer a friend. When they finish their first errand, you both win. Customers get cashback in their wallet, and runners earn commission credits. Points stay active as long as you use the app at least once every 6 months.",
	},
];

const faqSchema = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: faqItems.map((item) => ({
		"@type": "Question",
		name: item.question,
		acceptedAnswer: {
			"@type": "Answer",
			text: item.answer,
		},
	})),
};

function AccordionItem({
	question,
	answer,
	isOpen,
	onToggle,
}: {
	question: string;
	answer: string;
	isOpen: boolean;
	onToggle: () => void;
}) {
	return (
		<div className="border border-gray-200 rounded-2xl bg-surface overflow-hidden transition-all hover:border-primary/50">
			<button
				type="button"
				className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
				aria-expanded={isOpen}
				onClick={onToggle}
			>
				<span className="text-lg font-bold text-dark">{question}</span>
				<CaretDown
					size={20}
					weight="bold"
					className={cn(
						"text-slate transition-transform duration-300 shrink-0 ml-4",
						isOpen && "rotate-180",
					)}
				/>
			</button>
			<div
				className={cn(
					"accordion-content bg-white px-6 pb-6",
					isOpen && "active",
				)}
			>
				<p className="text-slate leading-relaxed pt-2 border-t border-gray-100">
					{answer}
				</p>
			</div>
		</div>
	);
}

export function FAQ() {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	return (
		<section id="faq" className="py-24 lg:py-32 bg-white px-4">
			<script
				type="application/ld+json"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD schema
				dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
			/>
			<div className="max-w-3xl mx-auto">
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-extrabold text-dark mb-6">
						Got questions? <br />
						<span className="font-script text-primary text-4xl md:text-5xl font-normal">
							We've got answers.
						</span>
					</h2>
				</div>

				<div className="space-y-4">
					{faqItems.map((item, index) => (
						<AccordionItem
							key={item.question}
							question={item.question}
							answer={item.answer}
							isOpen={openIndex === index}
							onToggle={() => setOpenIndex(openIndex === index ? null : index)}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
